import {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  type ReactNode,
  useCallback,
  type FC,
} from 'react'
import { Toast } from '../../components/common/Toast.tsx'

interface ToastContextType {
  readonly showToast: (message: string, duration?: number) => void
}

const ToastContext = createContext<ToastContextType | undefined>(undefined)

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used within a ToastProvider')
  return context
}

export const ToastProvider: FC<{ readonly children: ReactNode }> = ({
  children,
}) => {
  const [message, setMessage] = useState<string | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const showToast = useCallback((msg: string, duration?: number) => {
    // Clear any existing timeout before setting a new one
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    setMessage(msg)
    timeoutRef.current = setTimeout(() => {
      setMessage(null)
      timeoutRef.current = null
    }, duration ?? 3000)
  }, [])

  const handleClose = useCallback(() => {
    // Clear timeout when toast is manually closed
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setMessage(null)
  }, [])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div className="fixed right-4 top-28 z-50">
        {message && <Toast message={message} onClose={handleClose} />}
      </div>
    </ToastContext.Provider>
  )
}
